import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ALL_LESSONS } from "@/data/courseSearchData";

const B = {
  gold: "#D4A843", goldFaint: "#D4A84318",
  blue: "#4A90D9", blueFaint: "#4A90D918",
  green: "#43B581", red: "#E74C3C",
  bg: "#0D0F13", card: "#13161C",
  border: "#1E222A", white: "#F0F0F0",
  whiteGray: "#A0A8B8", gray: "#6B7280",
};

export default function SearchLessons({ user, hasAccess, bookmarkIds, onToggleBookmark }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const onSearch = (val) => {
    setQuery(val);
    if (!val.trim()) { setResults([]); setOpen(false); return; }
    const q = val.toLowerCase();
    const filtered = ALL_LESSONS.filter(l =>
      l.title.toLowerCase().includes(q) ||
      l.tag.toLowerCase().includes(q) ||
      l.track.toLowerCase().includes(q) ||
      l.phaseLabel.toLowerCase().includes(q)
    ).slice(0, 12);
    setResults(filtered);
    setOpen(filtered.length > 0);
  };

  const goToLesson = (lesson) => {
    setOpen(false);
    setQuery("");
    navigate(`/${lesson.course}`);
  };

  const courseAccent = (course) => course === "beginner" ? B.blue : B.gold;

  return (
    <div ref={ref} data-testid="search-lessons" style={{ position: "relative", width: "100%", maxWidth: 560, margin: "0 auto" }}>
      <div style={{ position: "relative" }}>
        <svg style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={B.gray} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          data-testid="search-input"
          type="text"
          value={query}
          onChange={(e) => onSearch(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder="Search lessons, topics, tags..."
          style={{
            width: "100%", padding: "12px 16px 12px 40px", borderRadius: 10,
            border: `1px solid ${open ? B.gold + "60" : B.border}`, background: B.card,
            color: B.white, fontSize: 14, outline: "none", boxSizing: "border-box",
            transition: "border-color 0.2s",
          }}
        />
        {query && (
          <button
            data-testid="search-clear-btn"
            onClick={() => { setQuery(""); setResults([]); setOpen(false); }}
            style={{
              position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
              background: "transparent", border: "none", color: B.gray, cursor: "pointer",
              fontSize: 16, padding: 4, lineHeight: 1,
            }}
          >
            &#x2715;
          </button>
        )}
      </div>

      {open && (
        <div data-testid="search-results" style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
          background: B.card, border: `1px solid ${B.border}`, borderRadius: 12,
          maxHeight: 380, overflowY: "auto", zIndex: 100,
          boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
        }}>
          <div style={{ padding: "8px 14px 6px", fontSize: 11, color: B.gray, letterSpacing: 1, fontFamily: "monospace" }}>
            {results.length} RESULT{results.length !== 1 ? "S" : ""}
          </div>
          {results.map((r) => {
              const locked = user && hasAccess && !hasAccess(r.course);
              const isBookmarked = bookmarkIds && bookmarkIds.has(r.id);
            return (
              <div
                key={r.id}
                data-testid={`search-result-${r.id}`}
                style={{
                  display: "flex", alignItems: "center", padding: "10px 14px",
                  background: "transparent", borderTop: `1px solid ${B.border}`,
                  cursor: "pointer", textAlign: "left", transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = B.bg}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div style={{ flex: 1 }} onClick={() => goToLesson(r)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.5, padding: "2px 6px",
                      borderRadius: 4, background: courseAccent(r.course) + "20",
                      color: courseAccent(r.course),
                    }}>
                      {r.courseLabel}
                    </span>
                    <span style={{ fontSize: 10, color: B.gray }}>{r.track}</span>
                    {locked && <span style={{ fontSize: 10, color: B.red }}>Locked</span>}
                  </div>
                  <div style={{ fontSize: 13, color: B.white, fontWeight: 500, lineHeight: 1.4 }}>{r.title}</div>
                  <div style={{ display: "flex", gap: 10, marginTop: 3 }}>
                    <span style={{ fontSize: 10, color: B.gray }}>{r.phaseLabel}</span>
                    <span style={{ fontSize: 10, color: B.gold }}>{r.tag}</span>
                    <span style={{ fontSize: 10, color: B.gray }}>{r.duration}</span>
                  </div>
                </div>
                {user && onToggleBookmark && (
                  <button
                    data-testid={`bookmark-btn-${r.id}`}
                    onClick={(e) => { e.stopPropagation(); onToggleBookmark(r); }}
                    style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6, flexShrink: 0, marginLeft: 4 }}
                    title={isBookmarked ? "Remove bookmark" : "Bookmark this lesson"}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={isBookmarked ? B.gold : "none"} stroke={isBookmarked ? B.gold : B.gray} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
